import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
  TooltipItem,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import { StockDataPoint } from '../data/mockStockData';
import { TechnicalIndicators } from '../utils/technicalIndicators';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface StockChartProps {
  data: StockDataPoint[];
  technicalIndicators: TechnicalIndicators;
  chartType: 'line' | 'candlestick';
  showEMA12: boolean;
  showEMA26: boolean;
  showEMA50: boolean;
  showEMA200: boolean;
  showVolume: boolean;
  symbol: string;
  theme: 'light' | 'dark';
}

export const StockChart: React.FC<StockChartProps> = ({
  data,
  technicalIndicators,
  chartType,
  showEMA12,
  showEMA26,
  showEMA50,
  showEMA200,
  showVolume,
  symbol,
  theme
}) => {
  // Price chart data
  const priceChartData = useMemo(() => {
    const datasets: any[] = [];

    // Main price line
    if (chartType === 'line') {
      datasets.push({
        label: `${symbol} Price`,
        data: data.map(point => ({
          x: point.timestamp,
          y: point.close
        })),
        borderColor: theme === 'dark' ? '#00D2FF' : '#0066FF',
        backgroundColor: theme === 'dark' ? 'rgba(0, 210, 255, 0.1)' : 'rgba(0, 102, 255, 0.1)',
        borderWidth: 2.5,
        fill: false,
        pointRadius: 0,
        pointHoverRadius: 5,
        tension: 0.1
      });
    }

    // EMA lines
    if (showEMA12 && technicalIndicators.ema12.length > 0) {
      datasets.push({
        label: 'EMA 12',
        data: technicalIndicators.ema12.map(point => ({
          x: point.timestamp,
          y: point.value
        })),
        borderColor: '#FF6B35',
        backgroundColor: 'transparent',
        borderWidth: 2,
        fill: false,
        pointRadius: 0,
        pointHoverRadius: 4,
        tension: 0.1
      });
    }

    if (showEMA26 && technicalIndicators.ema26.length > 0) {
      datasets.push({
        label: 'EMA 26',
        data: technicalIndicators.ema26.map(point => ({
          x: point.timestamp,
          y: point.value
        })),
        borderColor: '#F7931E',
        backgroundColor: 'transparent',
        borderWidth: 2,
        fill: false,
        pointRadius: 0,
        pointHoverRadius: 4,
        tension: 0.1
      });
    }

    if (showEMA50 && technicalIndicators.ema50.length > 0) {
      datasets.push({
        label: 'EMA 50',
        data: technicalIndicators.ema50.map(point => ({
          x: point.timestamp,
          y: point.value
        })),
        borderColor: '#A855F7',
        backgroundColor: 'transparent',
        borderWidth: 2,
        fill: false,
        pointRadius: 0,
        pointHoverRadius: 4,
        tension: 0.1
      });
    }

    if (showEMA200 && technicalIndicators.ema200.length > 0) {
      datasets.push({
        label: 'EMA 200',
        data: technicalIndicators.ema200.map(point => ({
          x: point.timestamp,
          y: point.value
        })),
        borderColor: '#EF4444',
        backgroundColor: 'transparent',
        borderWidth: 2.5,
        fill: false,
        pointRadius: 0,
        pointHoverRadius: 4,
        tension: 0.1
      });
    }

    return { datasets };
  }, [data, technicalIndicators, chartType, showEMA12, showEMA26, showEMA50, showEMA200, symbol, theme]);

  // Volume chart data
  const volumeChartData = useMemo(() => {
    if (!showVolume) return { datasets: [] };
    
    return {
      datasets: [
        {
          label: 'Volume',
          data: data.map(point => ({
            x: point.timestamp,
            y: point.volume
          })),
          backgroundColor: data.map((point, index) => {
            if (index === 0) return theme === 'dark' ? 'rgba(34, 197, 94, 0.6)' : 'rgba(22, 163, 74, 0.6)';
            const prevClose = data[index - 1].close;
            const currentClose = point.close;
            return currentClose >= prevClose 
              ? (theme === 'dark' ? 'rgba(34, 197, 94, 0.6)' : 'rgba(22, 163, 74, 0.6)') // Green for up
              : (theme === 'dark' ? 'rgba(239, 68, 68, 0.6)' : 'rgba(220, 38, 38, 0.6)'); // Red for down
          }),
          borderColor: 'transparent',
          borderWidth: 0,
        }
      ]
    };
  }, [data, showVolume, theme]);

  // Price chart options
  const priceOptions: ChartOptions<'line'> = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: 'index',
    },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          color: theme === 'dark' ? '#E5E7EB' : '#374151',
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12,
            weight: 'bold' as const
          }
        }
      },
      title: {
        display: true,
        text: `${symbol} - Price Chart with Technical Indicators`,
        color: theme === 'dark' ? '#F9FAFB' : '#111827',
        font: {
          size: 16,
          weight: 'bold' as const
        },
        padding: 20
      },
      tooltip: {
        backgroundColor: theme === 'dark' ? '#1F2937' : '#FFFFFF',
        titleColor: theme === 'dark' ? '#F9FAFB' : '#111827',
        bodyColor: theme === 'dark' ? '#E5E7EB' : '#374151',
        borderColor: theme === 'dark' ? '#374151' : '#D1D5DB',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          title: (tooltipItems: TooltipItem<'line'>[]) => {
            if (tooltipItems.length > 0) {
              const date = new Date(tooltipItems[0].parsed.x);
              return date.toLocaleDateString('en-US', {
                weekday: 'short',
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              });
            }
            return '';
          },
          label: (context: TooltipItem<'line'>) => {
            const label = context.dataset.label || '';
            const value = typeof context.parsed.y === 'number' 
              ? context.parsed.y.toFixed(2) 
              : context.parsed.y;
            return `${label}: $${value}`;
          }
        }
      }
    },
    scales: {
      x: {
        type: 'time',
        time: {
          displayFormats: {
            day: 'MMM dd',
            week: 'MMM dd',
            month: 'MMM yyyy'
          }
        },
        grid: {
          color: theme === 'dark' ? '#374151' : '#E5E7EB',
          drawOnChartArea: true,
          drawTicks: true
        },
        ticks: {
          color: theme === 'dark' ? '#9CA3AF' : '#6B7280',
          maxTicksLimit: 12,
          font: {
            size: 11
          }
        }
      },
      y: {
        type: 'linear',
        position: 'right',
        grid: {
          color: theme === 'dark' ? '#374151' : '#E5E7EB',
          drawOnChartArea: true,
          drawTicks: true
        },
        ticks: {
          color: theme === 'dark' ? '#9CA3AF' : '#6B7280',
          font: {
            size: 11
          },
          callback: function(value) {
            return '$' + (typeof value === 'number' ? value.toFixed(2) : value);
          }
        }
      }
    },
    elements: {
      point: {
        hoverBackgroundColor: theme === 'dark' ? '#00D2FF' : '#0066FF'
      }
    }
  }), [symbol, theme]);

  // Volume chart options
  const volumeOptions: ChartOptions<'bar'> = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: 'index',
    },
    plugins: {
      legend: {
        display: false
      },
      title: {
        display: true,
        text: 'Volume',
        color: theme === 'dark' ? '#F9FAFB' : '#111827',
        font: {
          size: 14,
          weight: 'bold' as const
        },
        padding: 10
      },
      tooltip: {
        backgroundColor: theme === 'dark' ? '#1F2937' : '#FFFFFF',
        titleColor: theme === 'dark' ? '#F9FAFB' : '#111827',
        bodyColor: theme === 'dark' ? '#E5E7EB' : '#374151',
        borderColor: theme === 'dark' ? '#374151' : '#D1D5DB',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          title: (tooltipItems: TooltipItem<'bar'>[]) => {
            if (tooltipItems.length > 0) {
              const date = new Date(tooltipItems[0].parsed.x);
              return date.toLocaleDateString('en-US', {
                weekday: 'short',
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              });
            }
            return '';
          },
          label: (context: TooltipItem<'bar'>) => {
            const value = typeof context.parsed.y === 'number' 
              ? context.parsed.y.toLocaleString() 
              : context.parsed.y;
            return `Volume: ${value}`;
          }
        }
      }
    },
    scales: {
      x: {
        type: 'time',
        time: {
          displayFormats: {
            day: 'MMM dd',
            week: 'MMM dd',
            month: 'MMM yyyy'
          }
        },
        grid: {
          color: theme === 'dark' ? '#374151' : '#E5E7EB',
          drawOnChartArea: false,
          drawTicks: true
        },
        ticks: {
          color: theme === 'dark' ? '#9CA3AF' : '#6B7280',
          maxTicksLimit: 12,
          font: {
            size: 11
          }
        }
      },
      y: {
        type: 'linear',
        position: 'right',
        grid: {
          color: theme === 'dark' ? '#374151' : '#E5E7EB',
          drawOnChartArea: true,
          drawTicks: true
        },
        ticks: {
          color: theme === 'dark' ? '#9CA3AF' : '#6B7280',
          font: {
            size: 11
          },
          callback: function(value) {
            return typeof value === 'number' ? (value / 1000000).toFixed(1) + 'M' : value;
          }
        }
      }
    }
  }), [theme]);

  return (
    <div 
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '1.25rem'
      }}
    >
      {/* Main Price Chart */}
      <div 
        className="price-chart-container"
        style={{
          height: '25rem',
          backgroundColor: theme === 'dark' ? '#111827' : '#FFFFFF',
          border: `1px solid ${theme === 'dark' ? '#374151' : '#D1D5DB'}`,
          borderRadius: '0.75rem',
          padding: '1.25rem',
          boxShadow: theme === 'dark'
            ? '0 4px 6px -1px rgba(0, 0, 0, 0.3)'
            : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Line data={priceChartData} options={priceOptions} />
      </div>

      {/* Volume Chart */}
      {showVolume && (
        <div 
          className="volume-chart-container"
          style={{
            height: '12.5rem',
            backgroundColor: theme === 'dark' ? '#111827' : '#FFFFFF',
            border: `1px solid ${theme === 'dark' ? '#374151' : '#D1D5DB'}`,
            borderRadius: '0.75rem',
            padding: '1.25rem',
            boxShadow: theme === 'dark'
              ? '0 4px 6px -1px rgba(0, 0, 0, 0.3)'
              : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Bar data={volumeChartData} options={volumeOptions} />
        </div>
      )}
    </div>
  );
};




